import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

interface TradingPresetSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  symbol: string;
  onSave: (
    presets: { default: number[]; coin: number[] },
    isDefault: boolean,
  ) => void;
  initialPresets?: { default: number[]; coin: number[] };
  initialIsDefault?: boolean;
}

export default function TradingPresetSettings({
  isOpen,
  onClose,
  symbol,
  onSave,
  initialPresets = {
    default: [1000, 2000, 3000, 4000, 5000],
    coin: [1000, 2000, 3000, 4000, 5000],
  },
  initialIsDefault = true,
}: TradingPresetSettingsProps) {
  const [presets, setPresets] = useState<{ default: number[]; coin: number[] }>(
    initialPresets,
  );
  const [isDefault, setIsDefault] = useState(initialIsDefault);

  useEffect(() => {
    if (isOpen) {
      setPresets(initialPresets);
      setIsDefault(initialIsDefault);
    }
  }, [isOpen, initialPresets, initialIsDefault]);

  const handlePresetChange = (index: number, value: string) => {
    const newPresets = { ...presets };
    const presetType = isDefault ? "default" : "coin";
    newPresets[presetType] = [...newPresets[presetType]];
    newPresets[presetType][index] = Number(value) || 0;
    setPresets(newPresets);
  };

  const handleSave = () => {
    onSave(presets, isDefault);
    onClose();
  };

  const currentPresets = isDefault ? presets.default : presets.coin;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Trading Presets</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="space-y-2">
            <Label>Preset Type</Label>
            <RadioGroup
              value={isDefault ? "default" : "coin"}
              onValueChange={(value) => setIsDefault(value === "default")}
              className="flex space-x-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="default" id="default" />
                <Label htmlFor="default">Default</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="coin" id="coin" />
                <Label htmlFor="coin">Coin: {symbol}</Label>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label>Preset Values ($)</Label>
            {currentPresets.map((preset, index) => (
              <div key={index} className="flex items-center space-x-2">
                <Label className="w-8">#{index + 1}</Label>
                <Input
                  type="number"
                  min="0"
                  step="any"
                  value={preset}
                  onChange={(e) => handlePresetChange(index, e.target.value)}
                  className="flex-1"
                />
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-end space-x-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
